import {
  defineMessage,
  defineMessages,
  type MessageDescriptor,
} from 'react-intl';

import {
  NoteContextKey,
  NoteDentition,
  NoteNumberQualifier,
  NoteTableCellType,
  NoteTeethGroup,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import type {
  ConsultationStatus,
  PartnerExportRefusal,
} from '@/shared/api/transcriptor';

import type { EncounterKind } from '@/entities/templates';
import { transcriptorMessages } from '@/entities/transcriptor';

import type {
  NoteBlockPaletteKind,
  NoteChoiceSelection,
  NoteTeethHolds,
} from '../lib';

import type { PartnerExportWarningKind } from './types';

// Labels of the teeth picker's shortcuts, in the interface language.
export const noteTeethGroupMessages = defineMessages<NoteTeethGroup>({
  [NoteTeethGroup.NoteTeethGroupInvalidValue]: {
    id: 'workspace.note.teeth.group',
    defaultMessage: 'Group',
  },
  [NoteTeethGroup.NoteTeethGroupUpper]: {
    id: 'workspace.note.teeth.upper',
    defaultMessage: 'Upper arch',
  },
  [NoteTeethGroup.NoteTeethGroupLower]: {
    id: 'workspace.note.teeth.lower',
    defaultMessage: 'Lower arch',
  },
  [NoteTeethGroup.NoteTeethGroupUpperRight]: {
    id: 'workspace.note.teeth.upperRight',
    defaultMessage: 'Upper right quadrant',
  },
  [NoteTeethGroup.NoteTeethGroupUpperLeft]: {
    id: 'workspace.note.teeth.upperLeft',
    defaultMessage: 'Upper left quadrant',
  },
  [NoteTeethGroup.NoteTeethGroupLowerLeft]: {
    id: 'workspace.note.teeth.lowerLeft',
    defaultMessage: 'Lower left quadrant',
  },
  [NoteTeethGroup.NoteTeethGroupLowerRight]: {
    id: 'workspace.note.teeth.lowerRight',
    defaultMessage: 'Lower right quadrant',
  },
  [NoteTeethGroup.NoteTeethGroupRight]: {
    id: 'workspace.note.teeth.right',
    defaultMessage: 'Right side',
  },
  [NoteTeethGroup.NoteTeethGroupLeft]: {
    id: 'workspace.note.teeth.left',
    defaultMessage: 'Left side',
  },
});

// Teeth groups as the note prints them, read in the template language:
// scribe's render labels (output_language.go) word for word, so the text the
// client prints before scribe answers is the text scribe then prints.
export const noteTeethGroupTextMessages = defineMessages<NoteTeethGroup>({
  [NoteTeethGroup.NoteTeethGroupInvalidValue]: {
    id: 'workspace.note.teeth.text.group',
    defaultMessage: 'group',
  },
  [NoteTeethGroup.NoteTeethGroupUpper]: {
    id: 'workspace.note.teeth.text.upper',
    defaultMessage: 'upper arch',
  },
  [NoteTeethGroup.NoteTeethGroupLower]: {
    id: 'workspace.note.teeth.text.lower',
    defaultMessage: 'lower arch',
  },
  [NoteTeethGroup.NoteTeethGroupUpperRight]: {
    id: 'workspace.note.teeth.text.upperRight',
    defaultMessage: 'upper right quadrant',
  },
  [NoteTeethGroup.NoteTeethGroupUpperLeft]: {
    id: 'workspace.note.teeth.text.upperLeft',
    defaultMessage: 'upper left quadrant',
  },
  [NoteTeethGroup.NoteTeethGroupLowerLeft]: {
    id: 'workspace.note.teeth.text.lowerLeft',
    defaultMessage: 'lower left quadrant',
  },
  [NoteTeethGroup.NoteTeethGroupLowerRight]: {
    id: 'workspace.note.teeth.text.lowerRight',
    defaultMessage: 'lower right quadrant',
  },
  [NoteTeethGroup.NoteTeethGroupRight]: {
    id: 'workspace.note.teeth.text.right',
    defaultMessage: 'right side',
  },
  [NoteTeethGroup.NoteTeethGroupLeft]: {
    id: 'workspace.note.teeth.text.left',
    defaultMessage: 'left side',
  },
});

export const noteContextKeyMessages = defineMessages<NoteContextKey>({
  [NoteContextKey.NoteContextKeyInvalidValue]: {
    id: 'workspace.note.context.unknown',
    defaultMessage: 'Value from the record',
  },
  [NoteContextKey.NoteContextKeyClinicianName]: {
    id: 'workspace.note.context.clinicianName',
    defaultMessage: 'Clinician name',
  },
  [NoteContextKey.NoteContextKeyVisitDate]: {
    id: 'workspace.note.context.visitDate',
    defaultMessage: 'Visit date',
  },
  [NoteContextKey.NoteContextKeyPatientName]: {
    id: 'workspace.note.context.patientName',
    defaultMessage: 'Patient name',
  },
  [NoteContextKey.NoteContextKeyPatientDateOfBirth]: {
    id: 'workspace.note.context.patientDateOfBirth',
    defaultMessage: 'Patient date of birth',
  },
});

export const noteBlockKindMessages = defineMessages<NoteBlockPaletteKind>({
  text: { id: 'workspace.noteEditor.kind.text', defaultMessage: 'Text' },
  options: {
    id: 'workspace.noteEditor.kind.options',
    defaultMessage: 'Options',
  },
  teeth: { id: 'workspace.noteEditor.kind.teeth', defaultMessage: 'Teeth' },
  freeText: {
    id: 'workspace.noteEditor.kind.freeText',
    defaultMessage: 'Note',
  },
  date: { id: 'workspace.noteEditor.kind.date', defaultMessage: 'Date' },
  number: { id: 'workspace.noteEditor.kind.number', defaultMessage: 'Number' },
  table: { id: 'workspace.noteEditor.kind.table', defaultMessage: 'Table' },
  context: {
    id: 'workspace.noteEditor.kind.context',
    defaultMessage: 'Value from the record',
  },
});

export const noteChoiceSelectionMessages = defineMessages<NoteChoiceSelection>({
  one: { id: 'workspace.blockEditor.selection.one', defaultMessage: 'One' },
  several: {
    id: 'workspace.blockEditor.selection.several',
    defaultMessage: 'Several',
  },
});

export const noteDentitionMessages = defineMessages<NoteDentition>({
  [NoteDentition.NoteDentitionInvalidValue]: {
    id: 'workspace.noteEditor.dentition.permanent',
    defaultMessage: 'Permanent teeth',
  },
  [NoteDentition.NoteDentitionPermanent]: {
    id: 'workspace.noteEditor.dentition.permanent',
    defaultMessage: 'Permanent teeth',
  },
  [NoteDentition.NoteDentitionPrimary]: {
    id: 'workspace.noteEditor.dentition.primary',
    defaultMessage: 'Primary teeth',
  },
  [NoteDentition.NoteDentitionMixed]: {
    id: 'workspace.noteEditor.dentition.mixed',
    defaultMessage: 'Mixed dentition',
  },
});

// A number's units (NOTE_UNITS) in the settings, in the interface language.
export const noteUnitMessages = defineMessages({
  mm: { id: 'workspace.blockEditor.unit.mm', defaultMessage: 'Millimetres' },
  cm: { id: 'workspace.blockEditor.unit.cm', defaultMessage: 'Centimetres' },
  mL: {
    id: 'workspace.blockEditor.unit.millilitres',
    defaultMessage: 'Millilitres',
  },
  deg: { id: 'workspace.blockEditor.unit.deg', defaultMessage: 'Degrees' },
  '%': { id: 'workspace.blockEditor.unit.percent', defaultMessage: 'Percent' },
  d: { id: 'workspace.blockEditor.unit.days', defaultMessage: 'Days' },
  wk: { id: 'workspace.blockEditor.unit.weeks', defaultMessage: 'Weeks' },
  mo: { id: 'workspace.blockEditor.unit.months', defaultMessage: 'Months' },
  a: { id: 'workspace.blockEditor.unit.years', defaultMessage: 'Years' },
  '1': { id: 'workspace.blockEditor.unit.count', defaultMessage: 'Count' },
});

export const noteNumberQualifierMessages = defineMessages<NoteNumberQualifier>({
  [NoteNumberQualifier.NoteNumberQualifierInvalidValue]: {
    id: 'workspace.blockEditor.qualifier.right',
    defaultMessage: 'Right',
  },
  [NoteNumberQualifier.NoteNumberQualifierRight]: {
    id: 'workspace.blockEditor.qualifier.right',
    defaultMessage: 'Right',
  },
  [NoteNumberQualifier.NoteNumberQualifierLeft]: {
    id: 'workspace.blockEditor.qualifier.left',
    defaultMessage: 'Left',
  },
  [NoteNumberQualifier.NoteNumberQualifierUpper]: {
    id: 'workspace.blockEditor.qualifier.upper',
    defaultMessage: 'Upper',
  },
  [NoteNumberQualifier.NoteNumberQualifierLower]: {
    id: 'workspace.blockEditor.qualifier.lower',
    defaultMessage: 'Lower',
  },
  [NoteNumberQualifier.NoteNumberQualifierFront]: {
    id: 'workspace.blockEditor.qualifier.front',
    defaultMessage: 'Front',
  },
});

// The pictures an option may show (NOTE_OPTION_IMAGES).
export const noteOptionImageMessages = defineMessages({
  'profile.straight': {
    id: 'workspace.blockEditor.image.profileStraight',
    defaultMessage: 'Profile: straight',
  },
  'profile.convex': {
    id: 'workspace.blockEditor.image.profileConvex',
    defaultMessage: 'Profile: convex',
  },
  'profile.concave': {
    id: 'workspace.blockEditor.image.profileConcave',
    defaultMessage: 'Profile: concave',
  },
  'arch_form.parabola': {
    id: 'workspace.blockEditor.image.archParabola',
    defaultMessage: 'Arch: parabolic',
  },
  'arch_form.v_shaped': {
    id: 'workspace.blockEditor.image.archVShaped',
    defaultMessage: 'Arch: V-shaped',
  },
  'arch_form.trapezoid': {
    id: 'workspace.blockEditor.image.archTrapezoid',
    defaultMessage: 'Arch: trapezoid',
  },
  'arch_form.triangular': {
    id: 'workspace.blockEditor.image.archTriangular',
    defaultMessage: 'Arch: triangular',
  },
  'arch_form.saddle': {
    id: 'workspace.blockEditor.image.archSaddle',
    defaultMessage: 'Arch: saddle',
  },
  'arch_form.asymmetric': {
    id: 'workspace.blockEditor.image.archAsymmetric',
    defaultMessage: 'Arch: asymmetric',
  },
});

// What the cells of a table column hold.
export const noteTableCellTypeMessages = defineMessages<NoteTableCellType>({
  [NoteTableCellType.NoteTableCellTypeInvalidValue]: {
    id: 'workspace.blockEditor.cellType.text',
    defaultMessage: 'Text',
  },
  [NoteTableCellType.NoteTableCellTypeCheckbox]: {
    id: 'workspace.blockEditor.cellType.checkbox',
    defaultMessage: 'A box to tick',
  },
  [NoteTableCellType.NoteTableCellTypeDate]: {
    id: 'workspace.blockEditor.cellType.date',
    defaultMessage: 'Date',
  },
  [NoteTableCellType.NoteTableCellTypeText]: {
    id: 'workspace.blockEditor.cellType.text',
    defaultMessage: 'Text',
  },
  [NoteTableCellType.NoteTableCellTypeNumber]: {
    id: 'workspace.blockEditor.cellType.number',
    defaultMessage: 'Number',
  },
  [NoteTableCellType.NoteTableCellTypeTooth]: {
    id: 'workspace.blockEditor.cellType.tooth',
    defaultMessage: 'Tooth',
  },
});

export const noteTeethHoldsMessages = defineMessages<NoteTeethHolds>({
  teeth: {
    id: 'workspace.blockEditor.teethHolds.teeth',
    defaultMessage: 'The tooth alone',
  },
  codes: {
    id: 'workspace.blockEditor.teethHolds.codes',
    defaultMessage: 'Codes',
  },
  number: {
    id: 'workspace.blockEditor.teethHolds.number',
    defaultMessage: 'A number',
  },
});

export const encounterKindMessages = defineMessages<EncounterKind>({
  consultation: {
    id: 'workspace.kind.consultation',
    defaultMessage: 'Consultation',
  },
  treatment: { id: 'workspace.kind.treatment', defaultMessage: 'Treatment' },
});

export const encounterStatusMessages: Record<
  ConsultationStatus,
  MessageDescriptor
> = {
  created: transcriptorMessages.statusCreated,
  recording: defineMessage({
    id: 'workspace.status.recording',
    defaultMessage: 'Recording',
  }),
  processing: transcriptorMessages.statusProcessing,
  succeeded: defineMessage({
    id: 'workspace.status.done',
    defaultMessage: 'Done',
  }),
  failed: transcriptorMessages.statusFailed,
};

export const partnerExportRefusalMessages =
  defineMessages<PartnerExportRefusal>({
    notPartnerTemplate: {
      id: 'workspace.note.partnerRefusal.notPartnerTemplate',
      defaultMessage:
        "The note's template does not follow a {partner} template.",
    },
    unsupportedFormat: {
      id: 'workspace.note.partnerRefusal.unsupportedFormat',
      defaultMessage: "This {partner} template's format cannot be copied yet.",
    },
    sourceMismatch: {
      id: 'workspace.note.partnerRefusal.sourceMismatch',
      defaultMessage:
        'The template no longer matches the {partner} template it follows and has to be imported again.',
    },
  });

// "other" names a kind this client does not know yet.
export const partnerExportWarningMessages = defineMessages<
  PartnerExportWarningKind | 'other'
>({
  cannot_switch_off: {
    id: 'workspace.note.partnerWarning.cannotSwitchOff',
    defaultMessage:
      'The template cannot switch a value off, so it stays in the text',
  },
  date_not_parsed: {
    id: 'workspace.note.partnerWarning.dateNotParsed',
    defaultMessage: 'A date is not written as day.month.year and goes as text',
  },
  value_not_found: {
    id: 'workspace.note.partnerWarning.valueNotFound',
    defaultMessage:
      "A value is not among the template's options and goes as text",
  },
  text_not_exported: {
    id: 'workspace.note.partnerWarning.textNotExported',
    defaultMessage: 'A changed wording outside the template text is not copied',
  },
  line_order_not_exported: {
    id: 'workspace.note.partnerWarning.lineOrderNotExported',
    defaultMessage: 'The template keeps its own order of lines',
  },
  skipped_line_note_not_exported: {
    id: 'workspace.note.partnerWarning.skippedLineNoteNotExported',
    defaultMessage: 'The note to a skipped line is not copied',
  },
  skipped_line_block_not_exported: {
    id: 'workspace.note.partnerWarning.skippedLineBlockNotExported',
    defaultMessage: 'Text added to a skipped line is not copied',
  },
  added_line_not_exported: {
    id: 'workspace.note.partnerWarning.addedLineNotExported',
    defaultMessage:
      'A line added to a section without template text is not copied',
  },
  teeth_missing: {
    id: 'workspace.note.partnerWarning.teethMissing',
    defaultMessage: 'A line is copied without a tooth',
  },
  other: {
    id: 'workspace.note.partnerWarning.other',
    defaultMessage: 'A place of the note is not copied exactly',
  },
});

const duplicateElementMessage = defineMessage({
  id: 'workspace.templateEdit.issue.duplicateId',
  defaultMessage: 'This element repeats another one: remove the copy.',
});

const lineBreakMessage = defineMessage({
  id: 'workspace.templateEdit.issue.lineBreak',
  defaultMessage:
    'A line cannot hold a line break: press Enter at the end of a line to add one.',
});

// What a template issue means, by its kind: the editor's own and scribe's.
// A kind missing here reads as `templateIssueFallbackMessage`.
export const templateIssueMessages: Record<string, MessageDescriptor> = {
  duplicate_id: duplicateElementMessage,
  duplicate_temp_id: duplicateElementMessage,
  line_break_in_line: lineBreakMessage,
  line_break: lineBreakMessage,
  ...defineMessages({
    content_before_section: {
      id: 'workspace.templateEdit.issue.contentBeforeSection',
      defaultMessage: 'Text before the first section: move it into a section.',
    },
    unknown_node: {
      id: 'workspace.templateEdit.issue.unknownNode',
      defaultMessage:
        'This part of the document cannot be saved in a template.',
    },
    empty_text: {
      id: 'workspace.templateIssue.emptyText',
      defaultMessage: 'the wording is empty',
    },
    empty_line: {
      id: 'workspace.templateIssue.emptyLine',
      defaultMessage: 'the line holds nothing',
    },
    empty_section: {
      id: 'workspace.templateIssue.emptySection',
      defaultMessage: 'the section has no lines',
    },
    empty_section_title: {
      id: 'workspace.templateIssue.emptySectionTitle',
      defaultMessage: 'the section has no title',
    },
    duplicate_section_title: {
      id: 'workspace.templateIssue.duplicateSectionTitle',
      defaultMessage: 'another section has the same title',
    },
    duplicate_section_code: {
      id: 'workspace.templateIssue.duplicateSectionCode',
      defaultMessage: 'another section has the same code',
    },
    empty_value: {
      id: 'workspace.templateIssue.emptyValue',
      defaultMessage: 'an option has no wording',
    },
    empty_values: {
      id: 'workspace.templateIssue.emptyValues',
      defaultMessage: 'the field is empty: add its options, columns or rows',
    },
    duplicate_value: {
      id: 'workspace.templateIssue.duplicateValue',
      defaultMessage: 'a value is listed twice',
    },
    too_many: {
      id: 'workspace.templateIssue.tooMany',
      defaultMessage: 'there are more items than a template allows',
    },
    too_long: {
      id: 'workspace.templateIssue.tooLong',
      defaultMessage: 'the text is too long',
    },
    placeholder_in_text: {
      id: 'workspace.templateIssue.placeholderInText',
      defaultMessage: "the text holds a '{{…}}' placeholder: remove it",
    },
    field_not_allowed: {
      id: 'workspace.templateIssue.fieldNotAllowed',
      defaultMessage: 'a setting does not suit this field',
    },
    invalid_default: {
      id: 'workspace.templateIssue.invalidDefault',
      defaultMessage: 'the default value is not one the field takes',
    },
    invalid_subheading: {
      id: 'workspace.templateIssue.invalidSubheading',
      defaultMessage: 'a subheading holds only text and no show-when',
    },
    invalid_slot_id: {
      id: 'workspace.templateIssue.invalidSlotId',
      defaultMessage:
        'the slot ID takes Latin letters, digits and underscores, in parts joined by dots',
    },
    duplicate_slot_id: {
      id: 'workspace.templateIssue.duplicateSlotId',
      defaultMessage: 'another field has the same slot ID',
    },
    option_code_missing: {
      id: 'workspace.templateIssue.optionCodeMissing',
      defaultMessage:
        'an option has no code: give every option a code, or none',
    },
    invalid_option_code: {
      id: 'workspace.templateIssue.invalidOptionCode',
      defaultMessage: 'an option code is not written as the field requires',
    },
    duplicate_option_code: {
      id: 'workspace.templateIssue.duplicateOptionCode',
      defaultMessage: 'an option code is used twice',
    },
    unknown_option_code: {
      id: 'workspace.templateIssue.unknownOptionCode',
      defaultMessage: 'an option code is not in the code dictionary',
    },
    invalid_option_label: {
      id: 'workspace.templateIssue.invalidOptionLabel',
      defaultMessage:
        'a diagnosis label starts with its code: keep the name only',
    },
    option_image_missing: {
      id: 'workspace.templateIssue.optionImageMissing',
      defaultMessage: 'an option shown as a picture has no picture',
    },
    invalid_option_image: {
      id: 'workspace.templateIssue.invalidOptionImage',
      defaultMessage: 'an option’s picture key is not written as a key',
    },
    unknown_option_image: {
      id: 'workspace.templateIssue.unknownOptionImage',
      defaultMessage: 'an option names a picture that does not exist',
    },
    unknown_code_system: {
      id: 'workspace.templateIssue.unknownCodeSystem',
      defaultMessage: 'the code system is not known',
    },
    unknown_unit: {
      id: 'workspace.templateIssue.unknownUnit',
      defaultMessage: 'the unit is not known',
    },
    invalid_precision: {
      id: 'workspace.templateIssue.invalidPrecision',
      defaultMessage: 'the precision is not valid',
    },
    invalid_range: {
      id: 'workspace.templateIssue.invalidRange',
      defaultMessage: 'the range is not valid: check its minimum and maximum',
    },
    unknown_qualifier: {
      id: 'workspace.templateIssue.unknownQualifier',
      defaultMessage: 'a breakdown of the number is not known',
    },
    unknown_dentition: {
      id: 'workspace.templateIssue.unknownDentition',
      defaultMessage: 'the dentition does not suit the field',
    },
    unknown_teeth_group: {
      id: 'workspace.templateIssue.unknownTeethGroup',
      defaultMessage: 'a teeth group is not known',
    },
    unknown_tooth: {
      id: 'workspace.templateIssue.unknownTooth',
      defaultMessage: 'a tooth is not one of the field’s dentition',
    },
    duplicate_tooth: {
      id: 'workspace.templateIssue.duplicateTooth',
      defaultMessage: 'a tooth is listed twice',
    },
    unknown_list: {
      id: 'workspace.templateIssue.unknownList',
      defaultMessage: 'the clinic list is missing or deleted',
    },
    list_not_readable: {
      id: 'workspace.templateIssue.listNotReadable',
      defaultMessage: 'the list belongs to another clinic',
    },
    list_language_mismatch: {
      id: 'workspace.templateIssue.listLanguageMismatch',
      defaultMessage: 'the list is in another language than the template',
    },
    show_when_unknown_block: {
      id: 'workspace.templateIssue.showWhenUnknownBlock',
      defaultMessage: 'the line is opened by a field no longer in the template',
    },
    show_when_unknown_value: {
      id: 'workspace.templateIssue.showWhenUnknownValue',
      defaultMessage: 'the line is opened by an option its field no longer has',
    },
    show_when_wrong_kind: {
      id: 'workspace.templateIssue.showWhenWrongKind',
      defaultMessage: 'only a field with options can open a line',
    },
    show_when_self: {
      id: 'workspace.templateIssue.showWhenSelf',
      defaultMessage: 'a line cannot be opened by its own field',
    },
    show_when_empty: {
      id: 'workspace.templateIssue.showWhenEmpty',
      defaultMessage: 'the link names no option',
    },
    show_when_cycle: {
      id: 'workspace.templateIssue.showWhenCycle',
      defaultMessage: 'lines open each other in a circle',
    },
    warning_missing_guidance: {
      id: 'workspace.templateIssue.warningMissingGuidance',
      defaultMessage: 'the section has no AI instruction',
    },
    warning_unbalanced_guidance: {
      id: 'workspace.templateIssue.warningUnbalancedGuidance',
      defaultMessage:
        'a [ or ] has no pair, so it is printed; an AI hint in [ ] is never printed',
    },
    warning_single_value_choice: {
      id: 'workspace.templateIssue.warningSingleValueChoice',
      defaultMessage: 'the choice has a single option',
    },
    warning_unnamed_field: {
      id: 'workspace.templateIssue.warningUnnamedField',
      defaultMessage:
        'the field has no name: AI will hardly know what to fill in',
    },
    warning_other_value: {
      id: 'workspace.templateIssue.warningOtherValue',
      defaultMessage: 'an option repeats the built-in “Other”',
    },
  }),
};

export const templateIssueFallbackMessage = defineMessage({
  id: 'workspace.templateIssue.other',
  defaultMessage: 'this place of the template cannot be saved as it is',
});
